export const blogsData = [
  {
    id: 1,
    title: "Lynx: Tiktok's React Native killer?",
    date: "April 10, 2025",
    description:
      "Exploring Lynx, ByteDance's new cross-platform mobile development framework that aims to revolutionize the industry.",
    readTime: "12 min read",
    author: "Amit Kumar Ghosh",
    tags: [
      "Lynx",
      "Mobile Development",
      "Cross-Platform",
      "React Native",
      "Flutter",
      "TikTok",
      "ByteDance",
    ],
    content: (
      <>
        <div className="mb-8">
          <p className="mb-4 text-justify">
            The world of cross-platform mobile development is constantly
            evolving, and a recent entrant has been making waves: Lynx, a
            technology developed by TikTok's parent company, ByteDance. This
            isn't just another alternative; it's a comprehensive approach aiming
            to address the limitations of existing solutions like React Native
            and Flutter. Let's delve into what makes Lynx noteworthy.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            The Promise of a New Mobile Development Paradigm
          </h2>
          <p className="mb-4 text-justify">
            One of the core motivations behind Lynx is to overcome performance
            bottlenecks often encountered in React Native due to its
            single-threaded JavaScript queue. Lynx introduces a dual-threaded
            architecture with a framework thread and a UI thread, allowing for
            heavy data processing without blocking UI updates, potentially
            leading to smoother 60 FPS performance even on less powerful
            devices. This separation also enables more complex UI interactions
            without the lag sometimes experienced in other frameworks.
            Furthermore, Lynx isn't limited to just being a React Native
            alternative; it's designed to be a broader platform for building
            native UIs for both mobile and web from a single codebase.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Embracing Web Familiarity with Native Power
          </h2>
          <p className="mb-4 text-justify">
            Interestingly, Lynx allows developers to write markup and CSS much
            like they would for the web, with native support for CSS animations,
            transitions, selectors, and modern visual effects. This lowers the
            barrier to entry for web developers looking to build native mobile
            applications. Unlike Flutter, which uses a custom rendering engine,
            Lynx, similar to React Native, renders native UI elements. This
            commitment to native primitives is seen as crucial for delivering a
            high-quality user experience that doesn't feel like a disguised game
            engine, a point the speaker makes strongly against Flutter. However,
            Lynx also has the flexibility to switch to a custom renderer for
            pixel-perfect consistency across platforms, similar to Flutter's
            approach.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Faster Development Cycles and Scalability
          </h2>
          <p className="mb-4 text-justify">
            Lynx also tackles the challenges of app distribution and
            scalability. It leverages technologies like module federation,
            allowing different teams to own and deploy parts of the application
            independently without having to update the entire bundle. This,
            combined with the potential for over-the-air updates for non-native
            binding changes, promises faster iteration cycles and reduced
            reliance on lengthy app store review processes. The introduction of
            R Speedy, a build tool based on Rust, further aims to provide fast
            build times crucial for large-scale applications. Additionally, Lynx
            boasts excellent developer tooling, including full Chrome DevTools
            protocol support, a significant improvement over the debugging
            experience in React Native.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Conclusion
          </h2>
          <p className="mb-4 text-justify">
            Lynx represents a significant step forward in cross-platform mobile
            development, offering a compelling blend of web development
            familiarity with the performance and native feel of
            platform-specific applications. By addressing key pain points in
            existing frameworks, particularly around performance, developer
            experience, and scalability, Lynx has the potential to become a
            major player in the cross-platform landscape. While still early in
            its open-source journey, its production readiness at TikTok and the
            pedigree of its creators suggest a bright future. It's definitely a
            technology worth watching for anyone interested in building
            high-quality mobile apps efficiently.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Inspired By:
            <a
              key="1"
              href="https://youtu.be/No0k-TMRuMA?si=89YrKJOdn675uKIW"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-5 sm:ml-2 mt-1 sm:mt-0 px-2 py-1 font-medium text-red-600 hover:text-red-800 hover:bg-blue-100 rounded transition-colors"
            >
              [Link]
            </a>
          </h2>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Categories/Tags:
          </h2>
          <ul className="flex flex-wrap gap-2 text-sm text-blue-500 mb-4">
            <li>[Lynx]</li>
            <li>[Mobile Development]</li>
            <li>[Cross-Platform]</li>
            <li>[React Native]</li>
            <li>[Flutter]</li>
            <li>[TikTok]</li>
            <li>[ByteDance]</li>
          </ul>
        </div>
      </>
    ),
  },
  {
  "id": 7,
  "title": "Decoding the AI Landscape: A Guide to Key Models and Their Strengths",
  "date": "April 11, 2025",
  "description": "Navigate the world of Artificial Intelligence with this overview of prominent AI models and their specialized capabilities, from understanding language to generating images.",
  "readTime": "8 min read",
  "author": "Amit Kumar Ghosh",
  "tags": ["AI Models", "Large Language Models", "Generative AI", "Computer Vision", "Machine Learning"],
  "content": (
    <>
      <div className="mb-8">
        <p className="mb-4 text-justify">
          The field of Artificial Intelligence is rapidly evolving, with new models emerging constantly, each possessing unique strengths and excelling in different domains. Understanding the capabilities of these models is crucial for anyone looking to leverage AI effectively. In this blog post, we'll explore some of the key AI models and highlight what they do best.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          The Rise of Large Language Models (LLMs)
        </h2>
        <p className="mb-4 text-justify">
          Large Language Models have revolutionized how machines understand and generate human language. These models, trained on vast amounts of text data, exhibit remarkable abilities in various natural language processing tasks.
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong className="font-semibold">GPT-4o</strong>: Excels in multimodal tasks, seamlessly integrating text, image, audio, and video understanding. It's known for providing fast and accurate explanations, handling multilingual prompts effectively, and demonstrating strong visual reasoning capabilities.
          </li>
          <li>
            <strong className="font-semibold">Claude 3.7 Sonnet</strong>: Highly proficient in code generation and debugging, and demonstrates advanced reasoning with complex diagrams and technical documentation.
          </li>
          <li>
            <strong className="font-semibold">Gemini 2.5 Pro</strong>: Delivers high-quality responses and exhibits strong reasoning abilities across a range of topics.
          </li>
          <li>
            <strong className="font-semibold">BERT</strong>: Stands out for its contextual understanding of language, making it highly effective for tasks like search, text analysis, and question answering.
          </li>
          <li>
            <strong className="font-semibold">LLaMA</strong>: A versatile model adept at natural language processing, powering conversational AI, performing text analysis and summarization, assisting with coding tasks, and handling language translation.
          </li>
          <li>
            <strong className="font-semibold">PaLM 2</strong>: Known for its strong commonsense reasoning, coding proficiency across multiple languages, and accurate multilingual translation capabilities.
          </li>
        </ul>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          The Creative Power of Generative Models
        </h2>
        <p className="mb-4 text-justify">
          Generative AI models focus on creating new content, ranging from realistic images to synthetic data.
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong className="font-semibold">DALL-E</strong>: A leader in generating unique and imaginative images based on textual descriptions, opening up new possibilities for creative expression.
          </li>
          <li>
            <strong className="font-semibold">GANs (Generative Adversarial Networks)</strong>: A class of models designed to generate realistic synthetic data, which can be valuable for training other AI models and various simulation purposes.
          </li>
        </ul>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Seeing the World Through Computer Vision
        </h2>
        <p className="mb-4 text-justify">
          Computer vision models enable AI to "see" and interpret visual information from images and videos.
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong className="font-semibold">YOLO (various versions)</strong>: Excels in real-time object detection, image classification, and even pose estimation, making it crucial for applications like autonomous driving and surveillance.
          </li>
          <li>
            <strong className="font-semibold">CLIP</strong>: Possesses a strong understanding of the relationship between text and images, enabling effective image search and content moderation.
          </li>
        </ul>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Specialized AI Models
        </h2>
        <p className="mb-4 text-justify">
          Beyond the broad categories, numerous AI models are designed for specific tasks.
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong className="font-semibold">Whisper</strong>: A powerful model for multilingual speech recognition, capable of accurately transcribing audio even in noisy environments.
          </li>
          <li>
            <strong className="font-semibold">Recommendation Systems</strong>: Designed to provide personalized content suggestions to users based on their past behavior and preferences, crucial for e-commerce and content platforms.
          </li>
          <li>
            <strong className="font-semibold">Time Series Models</strong>: Specialized in analyzing and predicting sequential data patterns over time, essential for forecasting in finance, weather, and many other fields.
          </li>
          <li>
            <strong className="font-semibold">Reinforcement Learning Models</strong>: Learn through trial and error to make optimal decisions in complex environments, finding applications in robotics, game playing, and autonomous systems.
          </li>
          <li>
            <strong className="font-semibold">Graph Neural Networks</strong>: Designed to process data represented as graphs, making them suitable for tasks like social network analysis, drug discovery, and knowledge graph reasoning.
          </li>
        </ul>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Conclusion
        </h2>
        <p className="mb-4 text-justify">
          The landscape of AI models is diverse and constantly expanding. Each model brings unique capabilities to the table, driving innovation across various industries. By understanding their strengths, we can better harness the power of AI to solve complex problems and unlock new possibilities.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Explore Further:
        </h2>
        <ul className="list-disc pl-6 mb-4">
          <li>Learn more about specific models through their official documentation and research papers.</li>
          <li>Experiment with different AI platforms and APIs to experience their capabilities firsthand.</li>
          <li>Stay updated with the latest advancements in the rapidly evolving field of Artificial Intelligence.</li>
        </ul>
        <div>
          {/* Table of LLM Models */}
        <table className="table-auto w-full mb-6 border-collapse border border-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-200 px-4 py-2 text-left">Model</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Strong Side</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Official Link</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-200 px-4 py-2">GPT-4o</td>
              <td className="border border-gray-200 px-4 py-2">Multimodal understanding (text, image, audio, video)</td>
              <td className="border border-gray-200 px-4 py-2">
                <a href="https://openai.com/gpt-4o" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">openai.com/gpt-4o</a>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-2">Claude 3.7 Sonnet</td>
              <td className="border border-gray-200 px-4 py-2">Code generation & advanced reasoning with diagrams</td>
              <td className="border border-gray-200 px-4 py-2">
                <a href="https://www.anthropic.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">anthropic.com</a>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-2">Gemini 2.5 Pro</td>
              <td className="border border-gray-200 px-4 py-2">High-quality responses and robust reasoning</td>
              <td className="border border-gray-200 px-4 py-2">
                <a href="https://ai.google/models/gemini" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">ai.google/models/gemini</a>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-2">BERT</td>
              <td className="border border-gray-200 px-4 py-2">Contextual language understanding for search & QA</td>
              <td className="border border-gray-200 px-4 py-2">
                <a href="https://github.com/google-research/bert" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">github.com/google-research/bert</a>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-2">LLaMA</td>
              <td className="border border-gray-200 px-4 py-2">Versatile NLP tasks: conversation, summarization, translation</td>
              <td className="border border-gray-200 px-4 py-2">
                <a href="https://ai.facebook.com/blog/large-language-model-llama-meta-ai" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">facebook.com/llama</a>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-2">PaLM 2</td>
              <td className="border border-gray-200 px-4 py-2">Commonsense reasoning & multilingual coding</td>
              <td className="border border-gray-200 px-4 py-2">
                <a href="https://developers.generativeai.google/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">developers.generativeai.google</a>
              </td>
            </tr>
          </tbody>
        </table>

        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong className="font-semibold">GPT-4o</strong>: Excels in multimodal tasks, seamlessly integrating text, image, audio, and video understanding. It's known for providing fast and accurate explanations, handling multilingual prompts effectively, and demonstrating strong visual reasoning capabilities.
          </li>
          <li>
            <strong className="font-semibold">Claude 3.7 Sonnet</strong>: Highly proficient in code generation and debugging, and demonstrates advanced reasoning with complex diagrams and technical documentation.
          </li>
          <li>
            <strong className="font-semibold">Gemini 2.5 Pro</strong>: Delivers high-quality responses and exhibits strong reasoning abilities across a range of topics.
          </li>
          <li>
            <strong className="font-semibold">BERT</strong>: Stands out for its contextual understanding of language, making it highly effective for tasks like search, text analysis, and question answering.
          </li>
          <li>
            <strong className="font-semibold">LLaMA</strong>: A versatile model adept at natural language processing, powering conversational AI, performing text analysis and summarization, assisting with coding tasks, and handling language translation.
          </li>
          <li>
            <strong className="font-semibold">PaLM 2</strong>: Known for its strong commonsense reasoning, coding proficiency across multiple languages, and accurate multilingual translation capabilities.
          </li>
        </ul>
        </div>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Categories/Tags:
        </h2>
        <ul className="flex flex-wrap gap-2 text-sm text-blue-500 mb-4">
          <li>[AI Models]</li>
          <li>[Large Language Models]</li>
          <li>[Generative AI]</li>
          <li>[Computer Vision]</li>
          <li>[Machine Learning]</li>
        </ul>
      </div>
    </>
  )
  },
  {
    id: 2,
    title: "Why Has Reading Become a Struggle?",
    date: "April 12, 2025",
    description:
      "Exploring the challenges faced by modern students in reading comprehension and sustained reading.",
    readTime: "15 min read",
    author: "Amit Kumar Ghosh",
    tags: ["Reading", "Education", "Literacy", "Technology"],
    content: (
      <div className="mb-8">
        <p className="mb-4 text-justify">
          It's a concerning trend being reported by professors across the
          nation: students, even at the most prestigious universities, are
          struggling to read books. In one instance at Columbia University,
          students in a required class, expected to read about a book a week,
          confessed they simply couldn't manage it, with one student admitting
          to never having been required to read an entire book before. This
          isn't just a university problem; it reflects a broader societal shift
          where people aren't reading anymore, regardless of gender or
          educational background. What's behind this decline in reading? The
          sources point to a confluence of factors.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          The Impact of Early Reading Instruction
        </h2>
        <p className="mb-4 text-justify">
          One significant piece of the puzzle lies in how children have been
          taught to read. For a very long time, the dominant method was phonics,
          which focuses on building reading skills from individual sounds to
          complex words. However, starting around the 1960s, a new approach
          called whole language learning emerged, along with some successor
          ideas like balanced literacy. This method emphasized inferring the
          meaning of words from context, presented as a more natural way to
          learn. The idea was that children had a natural propensity to read and
          would essentially teach themselves with the right context. The
          problem? Whole language learning didn't work, and literacy rates fell
          in the countries that adopted it, including parts of the United
          States, Canada, the UK, New Zealand, and Australia. If foundational
          reading skills weren't properly developed, reading is never going to
          be something that you do for fun. It's never going to be pleasurable.
          It's going to feel really difficult and like a lot of work, naturally
          leading people to avoid it. While whole language learning and balanced
          literacy were never uncontested ideas, and more states and countries
          are bringing back phonics, much of the damage is already done for
          those who were never taught phonics. It's speculated that even
          students who make it to elite universities like Columbia, while likely
          able to read at grade level, may still struggle with the volume of
          reading if their foundational skills weren't strong. Therefore, part
          of the explanation for why fewer people are reading seems to stem from
          how they were taught in school.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          The Role of Educational Reforms and Testing
        </h2>
        <p className="mb-4 text-justify">
          However, the issue isn't solely about initial reading instruction.
          Educational reforms, most notably things like No Child Left Behind and
          Common Core in the United States, have also played a role. These
          reforms emphasized two things: informational text and standardized
          testing. When students were being taught how to engage with text, they
          were learning how to extract information from it. Some even suggested
          that books were outdated and that education should focus on the types
          of text encountered in real life, like articles and web pages. As an
          exercise, a student might be presented with an excerpt from a longer
          text. They would be asked to analyze the argument or talk about what
          evidence was presented in the text or even what the thesis statement
          was. A lot of this would happen in English classes where traditionally
          you would be first exposed to reading longer and more difficult books.
          So instead of learning things like analyzing symbolism or theme or
          being able to practice your close reading of text, you were mostly
          focused on extracting information from those excerpts. Doing that
          traditional English stuff like close reading builds something that we
          could call your reading stamina. When a student who is fully capable
          of reading says that they can't read a whole book in a week, it's
          likely because they haven't had a chance to develop their reading
          stamina. In fact, in one survey only 17% of Educators said that they
          were teaching primarily with whole text. Therefore, the inability of
          students at universities like Columbia to read whole books might be
          more related to this lack of training in sustained reading rather than
          just initial literacy issues. These students were able to get into
          some of the best colleges and universities in the world, but they were
          never taught how to be great readers. They were taught how to be great
          test takers. So when these students get to college and they're asked
          to read whole books, they can't do it because this is a completely
          different skill than they've been told that they need to develop in
          high school. So much of their education has been about optimizing
          themselves for standardized tests and then eventually to take
          something like the SAT or the act. If you're just trying to do really
          well on a standardized test, you need to focus on reading excerpts and
          being able to answer multiple choice questions or maybe write a five
          paragraph essay about what you just read. You don't have to focus on
          analyzing themes in Tolstoy Dostoevsky Dickens. You don't have to
          worry about reading great books. So then if you have a required
          freshman class that's about great novels in the western tradition,
          well this might be your first time being exposed to many of those
          great novels in the western tradition, and it's going to be hard
          because you're not used to it.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          The Pervasiveness of Digital Distractions
        </h2>
        <p className="mb-4 text-justify">
          Finally, we cannot ignore the ubiquitous presence of phones and other
          digital distractions. While many people actually do spend their days
          reading text online through social media, news articles, and captions.
          Platforms like TikTok are maybe the most addictive of these platforms,
          and practically every video has burned in captions, so people are
          constantly reading something when they are online. If you want to get
          any reading done, you have to be disciplined and you have to decide
          that your phone needs to stay inside some place where it can't
          distract you. Imagine that you're a college student and you've never
          read a full book in a week or really ever been asked to read a hard or
          long book for your education and your professor says Hey I want you to
          read Homer's Iliad in a week. Even if you're a good sport and you get
          your book, think about all the things that are there ready to distract
          you—other people or your phone or your laptop. Anything with the
          screen really is there and it's ready to just make you feel more
          stimulated because even if we're people who really like to read
          compared to doing other things, the act of reading is really boring.
          It is the antithesis of our sort of constant need to be over
          stimulated. A lot of schools have decided to start Banning smartphones
          from the classroom. Not only have we systematically failed students as
          we've taught them to read or not taught them to read in some cases,
          we've also just designed a world that is there to constantly demand
          our attention and that takes us away from those deeper reflective sort
          of focus heavy practices like reading good books.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Conclusion
        </h2>
        <p className="mb-4 text-justify">
          In conclusion, the decline in reading habits appears to be a
          multifaceted issue stemming from ineffective early reading instruction
          due to the adoption of whole language learning, an educational system
          that prioritized test-taking over deep reading and focused on
          informational excerpts, and a modern world filled with constant
          digital distractions that compete for our attention. While there's a
          move to reintroduce phonics in early education, the challenge of
          cultivating a love for and ability to engage in sustained reading in
          an attention-demanding world remains significant. Fewer and fewer
          people read books. You have your super readers who read tons of books
          and they keep the average numbers up but you have more and more people
          who decide that reading just isn't for them.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Inspired By:
          <a
            key="1"
            href="https://youtu.be/A3wJcF0t0bQ?si=HB8lcEaIndWGTG6y"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-5 sm:ml-2 mt-1 sm:mt-0 px-2 py-1 font-medium text-red-600 hover:text-red-800 hover:bg-blue-100 rounded transition-colors"
          >
            [Link]
          </a>
        </h2>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Categories/Tags:
        </h2>
        <ul className="flex flex-wrap gap-2 text-sm text-gray-500 mb-4">
          <li>[Reading]</li>
          <li>[Education]</li>
          <li>[Literacy]</li>
          <li>[Technology]</li>
        </ul>
      </div>
    ),
  },
  {
    id: 3,
    title: "This Blog Will Cheer You Up!",
    date: "April 22, 2025",
    description:
      "A warm, neuroscience-based guide offering low-effort, science-backed techniques to gently manage sadness.",
    readTime: "5 min read",
    author: "Amit Kumar Ghosh",
    tags: ["Mental Health", "Psychology", "Neuroscience", "Self‑Care"],
    content: (
      <div className="mb-8">
        <p className="mb-4 text-justify">
          If your brain were a neighborhood, your amygdala would be that
          overzealous security guard who jumps at every rustle of leaves. When
          sadness rolls in, it’s just the guard sounding the alarm. It doesn’t
          mean you’re broken—just biologically wired to notice threats
          (emotional or otherwise). Let’s explore how small, science-backed
          steps can help you coexist with your guard, rather than trying to
          silence it.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          1. Name It to Tame It: Emotional Labeling
        </h2>
        <p className="mb-4 text-justify">
          Your amygdala fires faster than a camera flash when it senses
          distress. Research shows that{" "}
          <strong>putting feelings into words</strong> dampens that rush of
          alarm in less than a minute (Lieberman et al., 2007).
          <em>Low‑effort action:</em> Pause, take a breath, and whisper or jot
          down a single word—“sad,” “heavy,” “lonely.” That one word is a
          neurochemical signal: “Okay, guard, we see you.” It doesn’t banish the
          feeling, but it tells your brain it’s acknowledged.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          2. Grounding Techniques: Anchor in the Present
        </h2>
        <p className="mb-4 text-justify">
          Sadness often feels like an echo from the past or a worry for
          tomorrow. Grounding interrupts that loop by using your body as a
          reference point.
          <em>Low‑effort action:</em> Try the <strong>5‑4‑3‑2‑1</strong>{" "}
          exercise:
          <li>
            1. List <strong>5 things</strong> you can see.{" "}
          </li>
          <li>
            2. Identify <strong>4 things</strong> you can touch.
          </li>
          <li>
            3. Notice <strong>3 sounds</strong> around you.
          </li>
          <li>
            4. Acknowledge <strong>2 smells</strong> (or two imagined scents).
          </li>
          <li>
            5. Taste <strong>1 thing</strong>—your breath, a sip of water, or a
            breath of fresh air.
          </li>
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          3. Micro‑Shifts and Neuroplasticity: Small Rewires
        </h2>
        <p className="mb-4 text-justify">
          Neuroplasticity isn’t just for big life changes; your brain updates in
          millisecond bursts, too. Tiny acts of novelty—like shifting posture or
          scanning a different part of the room—send fresh electrical signals
          through your neural highways.
          <em>Low‑effort action:</em> Adjust your seating angle by 15°. Look out
          a window at a tree, then at a ceiling corner. You’re literally nudging
          your brain to form a new connection, however fleeting.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          4. Breathing as a Brake: Diaphragmatic Calm
        </h2>
        <p className="mb-4 text-justify">
          When sadness sweeps in, your breath often flattens—shallow and quick.
          Slowing your exhale activates the parasympathetic system, your
          biologically built-in brake.
          <em>Low‑effort action:</em> Breathe in for a count of{" "}
          <strong>4</strong>, then breathe out for <strong>6</strong>. Repeat
          three times. No deep inhalations needed—just gentle, mindful exhales.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          5. Ritual of Tiny Rewards: Dopamine Doses
        </h2>
        <p className="mb-4 text-justify">
          Your brain rewards you with tiny hits of dopamine for unexpected or
          novel stimuli. You don’t need grand achievements to get these bursts.
          <em>Low‑effort action:</em> Place a sticky note in a random corner of
          your workspace with a doodle or a smiley face. Every time you spot it,
          your brain gets a micro-dose of “hey, that’s new.”
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Why This Matters (Without the Hype)
        </h2>
        <p className="mb-4 text-justify">
          None of these steps promise to banish sadness forever, and they’re not
          about forcing yourself into perpetual cheer. Instead, think of them as{" "}
          <strong>biological parachutes</strong>—tools that slow the emotional
          drop, give you options, and remind you that your nervous system is
          just doing its job.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Final Thought
        </h2>
        <p className="mb-4 text-justify">
          Sadness is not a glitch or moral failing; it’s part of our human
          software. Your brain’s circuitry—amygdala, prefrontal cortex, vagus
          nerve—interacts in loops designed for survival, connection, and yes,
          occasional melancholy. Normalizing sadness means accepting that these
          circuits sometimes run in slow zones. And that’s okay.
        </p>
      </div>
    ),
  },
  {
    id: 4,
    title:
      "Understanding Disjoint Set Union (Union-Find): A Powerful Data Structure",
    date: "May 15, 2025",
    description:
      "Dive into the Disjoint Set Union (Union-Find) data structure, learn how it works, its optimizations, and its applications in solving real-world problems like graph connectivity.",
    readTime: "12 min read",
    author: "Amit Kumar Ghosh",
    tags: ["Data Structures", "Algorithms", "Union-Find", "Graph Theory"],
    content: (
      <div className="mb-8">
        <p className="mb-4 text-justify">
          Have you ever wondered how social networks group friends or how
          algorithms detect cycles in a graph? The answer lies in a clever data
          structure called <strong>Disjoint Set Union (DSU)</strong>, also known
          as <strong>Union-Find</strong>. This powerful tool helps manage groups
          of elements efficiently, making it a favorite in computer science for
          solving problems involving connectivity and grouping. In this blog,
          we’ll explore what DSU is, how it works, its optimizations, and where
          it’s used—all in a way that’s easy to grasp!
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          What is Disjoint Set Union? The Basics
        </h2>
        <p className="mb-4 text-justify">
          Disjoint Set Union is a data structure that keeps track of a
          collection of elements divided into non-overlapping groups, called{" "}
          <strong>sets</strong>. Imagine a school where students are grouped
          into different clubs, and no student belongs to more than one club.
          DSU helps answer questions like: “Which club does a student belong
          to?” or “Can we merge two clubs into one?”
        </p>
        <p className="mb-4 text-justify">
          DSU is typically represented as a forest of trees, where each tree is
          a set, and the root of the tree is the <strong>representative</strong>{" "}
          (or leader) of that set. Internally, we use an array to track the
          parent of each element, making it easy to find the root or merge sets.
          The two main operations are:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Find</strong>: Identifies the root of the set an element
            belongs to.
          </li>
          <li>
            <strong>Union</strong>: Merges two sets into one.
          </li>
        </ul>
        <p className="mb-4 text-justify">
          These operations make DSU perfect for problems where you need to group
          elements dynamically, like connecting cities in a network or finding
          friend circles online.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Core Operations: Find, Union, and Path Compression
        </h2>
        <p className="mb-4 text-justify">
          Let’s break down how DSU works by exploring its core operations and a
          key optimization called <strong>Path Compression</strong>.
        </p>

        <h3 className="font-semibold text-lg mb-1">Find: Finding the Leader</h3>
        <p className="mb-4 text-justify">
          The <strong>Find</strong> operation locates the root (leader) of an
          element’s set. Starting from an element, we follow its parent pointers
          until we reach a node that is its own parent—the root. For example, if
          we have a chain like <code>0 → 1 → 4</code>, calling{" "}
          <code>Find(0)</code> traces through <code>0</code> to <code>1</code>{" "}
          to <code>4</code>, returning <code>4</code> as the root.
        </p>

        <h3 className="font-semibold text-lg mb-1">Union: Merging Groups</h3>
        <p className="mb-4 text-justify">
          The <strong>Union</strong> operation combines two sets into one. It
          works by:
          <ol className="list-decimal pl-6">
            <li>
              Finding the roots of the two elements’ sets using{" "}
              <code>Find</code>.
            </li>
            <li>Making one root the parent of the other, merging the trees.</li>
          </ol>
          For instance, to merge sets <code>[0,1]</code> (root <code>1</code>)
          and <code>{4}</code> (root <code>4</code>), we set{" "}
          <code>parent[4] = 1</code>, creating a single set{" "}
          <code>[0, 1, 4]</code>.
        </p>

        <h3 className="font-semibold text-lg mb-1">
          Path Compression: Speeding Things Up
        </h3>
        <p className="mb-4 text-justify">
          Without optimization, following long chains of parents can be slow.
          That’s where <strong>Path Compression</strong> comes in. During a{" "}
          <code>Find</code> operation, we make every element in the path point
          directly to the root, flattening the tree. So, if we have{" "}
          <code>0 → 1 → 4</code>, after <code>Find(0)</code>, it becomes{" "}
          <code>0 → 4</code>, <code>1 → 4</code>. This makes future{" "}
          <code>Find</code> calls lightning-fast!
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Optimization: Union by Rank
        </h2>
        <p className="mb-4 text-justify">
          To make DSU even more efficient, we use <strong>Union by Rank</strong>
          . Each set has a “rank,” which roughly represents the height of its
          tree. When merging two sets:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            If one set’s rank is higher, attach the shorter tree to the taller
            one.
          </li>
          <li>
            If ranks are equal, choose one as the parent and increase its rank
            by 1.
          </li>
        </ul>
        <p className="mb-4 text-justify">
          This keeps trees shallow, reducing the time for <code>Find</code>{" "}
          operations. Combined with Path Compression, it makes DSU incredibly
          efficient.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          A Practical Example: C++ Implementation
        </h2>
        <p className="mb-4 text-justify">
          Let’s see DSU in action with a simple C++ implementation that uses
          Path Compression and Union by Rank. This code is easy to understand
          and perfect for demonstrating how DSU works.
        </p>
        <pre className="bg-gray-100 p-4 rounded mb-4 overflow-x-auto">
          <code>
            {`#include <vector>
            class DSU {
            private:
                std::vector<int> parent; // Tracks the parent of each element
                std::vector<int> rank;   // Tracks the height (rank) of each set

            public:
                // Initialize DSU with n elements
                DSU(int n) {
                    parent.resize(n);
                    rank.resize(n, 0); // All ranks start at 0
                    for (int i = 0; i < n; ++i) {
                        parent[i] = i; // Each element is its own parent
                    }
                }

                // Find the root with Path Compression
                int find(int x) {
                    if (parent[x] != x) {
                        parent[x] = find(parent[x]); // Path Compression
                    }
                    return parent[x];
                }

                // Merge sets using Union by Rank
                void unionSets(int x, int y) {
                    int rootX = find(x);
                    int rootY = find(y);
                    if (rootX == rootY) return;
                    if (rank[rootX] > rank[rootY]) {
                        parent[rootY] = rootX;
                    } else if (rank[rootX] < rank[rootY]) {
                        parent[rootX] = rootY;
                    } else {
                        parent[rootY] = rootX;
                        rank[rootX]++;
                    }
                }
            };

            // Example usage
            #include <iostream>

            int main() {
                DSU dsu(5); // Create DSU with 5 elements (0, 1, 2, 3, 4)
                dsu.unionSets(0, 1); // Merge 0 and 1
                dsu.unionSets(2, 3); // Merge 2 and 3
                dsu.unionSets(1, 4); // Merge 1 and 4
                std::cout << "Root of 0: " << dsu.find(0) << std::endl;
                std::cout << "Root of 2: " << dsu.find(2) << std::endl;
                return 0;
            }`}
          </code>
        </pre>
        <p className="mb-4 text-justify">
          In this example, we create a DSU with 5 elements and perform unions to
          form sets <code>[0, 1, 4]</code> and <code>[2, 3]</code>. The{" "}
          <code>find</code> calls show the roots of the sets, demonstrating how
          DSU tracks groups.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Where is DSU Used? Real-World Applications
        </h2>
        <p className="mb-4 text-justify">
          DSU is a versatile tool used in many algorithms and applications,
          including:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Cycle Detection in Graphs</strong>: Check if adding an edge
            creates a cycle by verifying if two nodes are in the same set.
          </li>
          <li>
            <strong>Kruskal’s Algorithm</strong>: Builds a Minimum Spanning Tree
            (MST) by merging edges while avoiding cycles.
          </li>
          <li>
            <strong>Social Networks</strong>: Groups friends or connections,
            like finding friend circles on platforms like Facebook.
          </li>
          <li>
            <strong>Connected Components</strong>: Identifies distinct groups in
            an undirected graph.
          </li>
        </ul>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Time Complexity: How Fast is DSU?
        </h2>
        <p className="mb-4 text-justify">
          The efficiency of DSU depends on its optimizations:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Without Optimizations</strong>: <code>Find</code> and{" "}
            <code>Union</code> can take O(n) time in the worst case, where n is
            the number of elements.
          </li>
          <li>
            <strong>With Union by Rank</strong>: Reduces tree height to O(log
            n).
          </li>
          <li>
            <strong>With Path Compression + Union by Rank</strong>: Achieves an
            amortized time of O(α(n)) per operation, where α(n) is the inverse
            Ackermann function—essentially constant for all practical purposes.
          </li>
        </ul>
        <p className="mb-4 text-justify">
          This near-constant time makes DSU incredibly fast for large datasets,
          making it a go-to choice for many algorithms.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Conclusion
        </h2>
        <p className="mb-4 text-justify">
          Disjoint Set Union (Union-Find) is a fascinating and powerful data
          structure that simplifies complex problems involving grouping and
          connectivity. By mastering its core operations—<code>Find</code> and{" "}
          <code>Union</code>—and leveraging optimizations like Path Compression
          and Union by Rank, you can solve problems like cycle detection and
          Minimum Spanning Trees with ease. Whether you’re building a social
          network or analyzing graph structures, DSU is a tool you’ll want in
          your toolkit. Try implementing the code above and experiment with it
          to see DSU in action!
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Inspired By:
        </h2>
        <p className="mb-4">
          <a
            key="1"
            href="https://cp-algorithms.com/data_structures/disjoint_set_union.html"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-5 sm:ml-2 mt-1 sm:mt-0 px-2 py-1 font-medium text-red-600 hover:text-red-800 hover:bg-blue-100 rounded transition-colors"
          >
            [CP Algorithms: Disjoint Set Union]
          </a>
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Categories/Tags:
        </h2>
        <ul className="flex flex-wrap gap-2 text-sm text-gray-500 mb-4">
          <li>[Data Structures]</li>
          <li>[Algorithms]</li>
          <li>[Union-Find]</li>
          <li>[Graph Theory]</li>
        </ul>
      </div>
    ),
  },
  {
  id: 5,
  title: "ডিসজয়েন্ট সেট ইউনিয়ন (ইউনিয়ন-ফাইন্ড): একটি শক্তিশালী ডেটা স্ট্রাকচার",
  date: "১৫ মে, ২০২৫",
  description: "ডিসজয়েন্ট সেট ইউনিয়ন (ইউনিয়ন-ফাইন্ড) ডেটা স্ট্রাকচার সম্পর্কে জানুন, এটি কীভাবে কাজ করে, এর অপটিমাইজেশন, এবং গ্রাফ সংযোগের মতো সমস্যা সমাধানে এর ব্যবহার।",
  readTime: "১২ মিনিট ",
  author: "অমিত কুমার ঘোষ",
  tags: ["ডেটা স্ট্রাকচার", "অ্যালগরিদম", "ইউনিয়ন-ফাইন্ড", "গ্রাফ তত্ত্ব"],
  content: (
    <div className="mb-8">
      <p className="mb-4 text-justify">
        কখনো ভেবেছ কীভাবে সোশ্যাল নেটওয়ার্কে বন্ধুদের গ্রুপ তৈরি হয় বা গ্রাফে সাইকেল ধরা পড়ে? এর পিছনে রয়েছে একটি চমৎকার ডেটা স্ট্রাকচার, যার নাম <strong>ডিসজয়েন্ট সেট ইউনিয়ন (DSU)</strong>, যাকে আমরা <strong>ইউনিয়ন-ফাইন্ড</strong> নামেও ডাকি। এই ডেটা স্ট্রাকচার গ্রুপ পরিচালনার কাজ দ্রুত করে, তাই কম্পিউটার সায়েন্সে সংযোগ ও গ্রুপিংয়ের সমস্যা সমাধানে এটি খুব জনপ্রিয়। এই ব্লগে আমরা DSU কী, এটি কীভাবে কাজ করে, এর অপটিমাইজেশন এবং কোথায় ব্যবহৃত হয় তা সহজভাবে শিখব!
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">ডিসজয়েন্ট সেট ইউনিয়ন কী? বেসিক ধারণা</h2>
      <p className="mb-4 text-justify">
        ডিসজয়েন্ট সেট ইউনিয়ন হলো এমন একটি ডেটা স্ট্রাকচার যা উপাদানগুলোকে পৃথক পৃথক গ্রুপে বা <strong>সেটে</strong> ভাগ করে রাখে। কল্পনা করো, একটি স্কুলে ছাত্ররা বিভিন্ন ক্লাবে ভাগ হয়েছে, এবং কোনো ছাত্র একাধিক ক্লাবে নেই। DSU আমাদের এই প্রশ্নের উত্তর দেয়: “কোন ছাত্র কোন ক্লাবে আছে?” বা “দুটি ক্লাব কি এক করা যায়?”
      </p>
      <p className="mb-4 text-justify">
        DSU সাধারণত একটি বন (ফরেস্ট) হিসেবে তৈরি হয়, যেখানে প্রতিটি গাছ একটি সেট। গাছের <strong>রুট</strong> হলো সেই সেটের প্রতিনিধি বা নেতা। আমরা একটি <strong>প্যারেন্ট অ্যারে</strong> ব্যবহার করি উপাদানের প্যারেন্ট ট্র্যাক করতে। এর দুটি প্রধান অপারেশন:
      </p>
      <ul className="list-disc pl-6 mb-4">
        <li><strong>Find</strong>: কোনো উপাদানের সেটের রুট খুঁজে বের করে।</li>
        <li><strong>Union</strong>: দুটি সেটকে এক করে।</li>
      </ul>
      <p className="mb-4 text-justify">
        এই অপারেশনগুলো DSU কে এমন সমস্যার জন্য আদর্শ করে, যেখানে গ্রুপিং দরকার, যেমন শহরের নেটওয়ার্ক সংযোগ বা অনলাইনে বন্ধুদের গ্রুপ।
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">মূল অপারেশন: Find, Union, এবং Path Compression</h2>
      <p className="mb-4 text-justify">
        চলো, DSU কীভাবে কাজ করে তা এর মূল অপারেশন এবং <strong>Path Compression</strong> নামক একটি গুরুত্বপূর্ণ অপটিমাইজেশন দিয়ে বুঝি।
      </p>

      <h3 className="font-semibold text-lg mb-1">Find: নেতা খুঁজে বের করা</h3>
      <p className="mb-4 text-justify">
        <strong>Find</strong> অপারেশন কোনো উপাদানের সেটের রুট বা নেতা খুঁজে বের করে। আমরা উপাদান থেকে তার প্যারেন্টের দিকে যাই, যতক্ষণ না এমন একটি নোড পাই যার প্যারেন্ট সে নিজেই—এটাই রুট। উদাহরণস্বরূপ, যদি আমাদের থাকে <code>0 → 1 → 4</code>, তাহলে <code>Find(0)</code> করলে আমরা <code>0</code> থেকে <code>1</code>, তারপর <code>1</code> থেকে <code>4</code> যাবো এবং <code>4</code> পাবো।
      </p>

      <h3 className="font-semibold text-lg mb-1">Union: গ্রুপ এক করা</h3>
      <p className="mb-4 text-justify">
        <strong>Union</strong> অপারেশন দুটি সেটকে এক করে দেয়। এটি এভাবে কাজ করে:
        <ol className="list-decimal pl-6">
          <li><code>Find</code> দিয়ে দুটি উপাদানের রুট খুঁজি।</li>
          <li>একটি রুটকে অন্য রুটের প্যারেন্ট করে গাছগুলো এক করি।</li>
        </ol>
        উদাহরণ: <code>[0, 1]</code> (রুট <code>1</code>) এবং <code>{4}</code> (রুট <code>4</code>) এক করতে, আমরা <code>parent[4] = 1</code> করি, ফলে একটি সেট হয় <code>[0, 1, 4]</code>।
      </p>

      <h3 className="font-semibold text-lg mb-1">Path Compression: দ্রুত করার কৌশল</h3>
      <p className="mb-4 text-justify">
        লম্বা প্যারেন্ট চেইন থাকলে Find ধীর হতে পারে। তাই আমরা <strong>Path Compression</strong> ব্যবহার করি। <code>Find</code> করার সময়, পথের সব উপাদানকে সরাসরি রুটের সাথে যুক্ত করি, গাছ সমতল করে। যেমন, <code>0 → 1 → 4</code> থাকলে, <code>Find(0)</code> করার পর এটি হয় <code>0 → 4</code>, <code>1 → 4</code>। এর ফলে পরবর্তী <code>Find</code> দ্রুত হয়!
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">অপটিমাইজেশন: Union by Rank</h2>
      <p className="mb-4 text-justify">
        DSU কে আরও দ্রুত করতে আমরা <strong>Union by Rank</strong> ব্যবহার করি। প্রতিটি সেটের একটি “র‍্যাঙ্ক” থাকে, যা গাছের উচ্চতার মতো। দুটি সেট এক করার সময়:
      </p>
      <ul className="list-disc pl-6 mb-4">
        <li>বড় র‍্যাঙ্কের গাছের অধীনে ছোট র‍্যাঙ্কের গাছ যুক্ত করি।</li>
        <li>র‍্যাঙ্ক সমান হলে, একটিকে প্যারেন্ট করে এর র‍্যাঙ্ক ১ বাড়াই।</li>
      </ul>
      <p className="mb-4 text-justify">
        এটি গাছের উচ্চতা কমায়, ফলে <code>Find</code> অপারেশন দ্রুত হয়। Path Compression এর সাথে এটি DSU কে অত্যন্ত কার্যকর করে।
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">বাস্তব উদাহরণ: C++ কোড</h2>
      <p className="mb-4 text-justify">
        চলো, একটি সহজ C++ কোড দিয়ে DSU কীভাবে কাজ করে তা দেখি। এই কোডে Path Compression এবং Union by Rank ব্যবহৃত হয়েছে, এবং এটি বোঝা সহজ।
      </p>
      <pre className="bg-gray-100 p-4 rounded mb-4 overflow-x-auto">
        <code>
        {`#include <vector>

        class DSU {
        private:
            std::vector<int> parent; // প্রতিটি উপাদানের প্যারেন্ট ট্র্যাক করে
            std::vector<int> rank;   // প্রতিটি সেটের উচ্চতা (র‍্যাঙ্ক) ট্র্যাক করে

        public:
            // nটি উপাদান দিয়ে DSU শুরু করা
            DSU(int n) {
                parent.resize(n);
                rank.resize(n, 0); // সব র‍্যাঙ্ক শুরুতে ০
                for (int i = 0; i < n; ++i) {
                    parent[i] = i; // প্রতিটি উপাদান নিজেই তার প্যারেন্ট
                }
            }

            // Path Compression সহ রুট খুঁজে বের করা
            int find(int x) {
                if (parent[x] != x) {
                    parent[x] = find(parent[x]); // Path Compression
                }
                return parent[x];
            }

            // Union by Rank সহ সেট এক করা
            void unionSets(int x, int y) {
                int rootX = find(x);
                int rootY = find(y);
                if (rootX == rootY) return;
                if (rank[rootX] > rank[rootY]) {
                    parent[rootY] = rootX;
                } else if (rank[rootX] < rank[rootY]) {
                    parent[rootX] = rootY;
                } else {
                    parent[rootY] = rootX;
                    rank[rootX]++;
                }
            }
        };

        // উদাহরণ ব্যবহার
        #include <iostream>

        int main() {
            DSU dsu(5); // ৫টি উপাদান দিয়ে DSU তৈরি (০, ১, ২, ৩, ৪)
            dsu.unionSets(0, 1); // ০ এবং ১ এক করো
            dsu.unionSets(2, 3); // ২ এবং ৩ এক করো
            dsu.unionSets(1, 4); // ১ এবং ৪ এক করো
            std::cout << "০ এর রুট: " << dsu.find(0) << std::endl;
            std::cout << "২ এর রুট: " << dsu.find(2) << std::endl;
            return 0;
        }`}
        </code>
      </pre>
      <p className="mb-4 text-justify">
        এই উদাহরণে আমরা ৫টি উপাদান নিয়ে DSU তৈরি করেছি এবং ইউনিয়ন করে সেট তৈরি করেছি: <code>[0, 1, 4]</code> এবং <code>[2, 3]</code>। <code>find</code> কলগুলো সেটের রুট দেখায়, যা DSU এর গ্রুপ ট্র্যাকিং ক্ষমতা দেখায়।
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">DSU কোথায় ব্যবহৃত হয়? বাস্তব অ্যাপ্লিকেশন</h2>
      <p className="mb-4 text-justify">
        DSU অনেক কাজে ব্যবহৃত হয়, যেমন:
      </p>
      <ul className="list-disc pl-6 mb-4">
        <li><strong>গ্রাফে সাইকেল ধরা</strong>: দুটি নোড একই সেটে থাকলে এজ যোগ করলে সাইকেল হবে।</li>
        <li><strong>ক্রুস্কাল অ্যালগরিদম</strong>: মিনিমাম স্প্যানিং ট্রি (MST) তৈরি করতে।</li>
        <li><strong>সোশ্যাল নেটওয়ার্ক</strong>: ফেসবুকের মতো প্ল্যাটফর্মে বন্ধুদের গ্রুপ তৈরি।</li>
        <li><strong>কানেক্টেড কম্পোনেন্টস</strong>: গ্রাফে পৃথক গ্রুপ খুঁজে বের করা।</li>
      </ul>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">সময়ের জটিলতা: DSU কত দ্রুত?</h2>
      <p className="mb-4 text-justify">
        DSU এর দক্ষতা অপটিমাইজেশনের উপর নির্ভর করে:
      </p>
      <ul className="list-disc pl-6 mb-4">
        <li><strong>অপটিমাইজেশন ছাড়া</strong>: <code>Find</code> এবং <code>Union</code> সবচেয়ে খারাপ ক্ষেত্রে O(n) সময় নিতে পারে, যেখানে n হলো উপাদানের সংখ্যা।</li>
        <li><strong>Union by Rank সহ</strong>: গাছের উচ্চতা O(log n) হয়।</li>
        <li><strong>Path Compression + Union by Rank</strong>: প্রতি অপারেশনের গড় সময় O(α(n)), যেখানে α(n) ইনভার্স অ্যাকারম্যান ফাংশন—বাস্তবে প্রায় ধ্রুব।</li>
      </ul>
      <p className="mb-4 text-justify">
        এই প্রায় ধ্রুব সময়ের কারণে DSU বড় ডেটাসেটের জন্য দারুণ কাজ করে, তাই অনেক অ্যালগরিদমে এটি ব্যবহৃত হয়।
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">উপসংহার</h2>
      <p className="mb-4 text-justify">
        ডিসজয়েন্ট সেট ইউনিয়ন (ইউনিয়ন-ফাইন্ড) একটি আকর্ষণীয় এবং শক্তিশালী ডেটা স্ট্রাকচার যা গ্রুপিং এবং সংযোগের জটিল সমস্যা সহজ করে। এর মূল অপারেশন—<code>Find</code> এবং <code>Union</code>—এবং Path Compression ও Union by Rank এর মতো অপটিমাইজেশন শিখে তুমি সাইকেল ধরা বা মিনিমাম স্প্যানিং ট্রির মতো সমস্যা সহজেই সমাধান করতে পারবে। সোশ্যাল নেটওয়ার্ক হোক বা গ্রাফ বিশ্লেষণ, DSU একটি দরকারি টুল। উপরের কোড চালিয়ে এবং পরীক্ষা করে DSU কীভাবে কাজ করে তা নিজে দেখে নাও!
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">অনুপ্রেরণার উৎস:</h2>
      <p className="mb-4">
        <a
          key="1"
          href="https://cp-algorithms.com/data_structures/disjoint_set_union.html"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-5 sm:ml-2 mt-1 sm:mt-0 px-2 py-1 font-medium text-red-600 hover:text-red-800 hover:bg-blue-100 rounded transition-colors"
        >
          [CP Algorithms: ডিসজয়েন্ট সেট ইউনিয়ন]
        </a>
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">ক্যাটাগরি/ট্যাগ:</h2>
      <ul className="flex flex-wrap gap-2 text-sm text-gray-500 mb-4">
        <li>[ডেটা স্ট্রাকচার]</li>
        <li>[অ্যালগরিদম]</li>
        <li>[ইউনিয়ন-ফাইন্ড]</li>
        <li>[গ্রাফ তত্ত্ব]</li>
      </ul>
    </div>
  ),
  },
  {
  "id": 6,
  "title": "Introduction to Scratch Programming: Unlocking Creativity Through Code",
  "date": "May 20, 2025",
  "description": "Discover Scratch, MIT's visual programming platform, and learn how it empowers beginners to create games, animations, and stories while mastering fundamental programming concepts.",
  "readTime": "12 min read",
  "author": "Amit Kumar Ghosh",
  "tags": ["Scratch", "Programming", "Education", "Kids", "Coding", "Game Development", "Computer Science"],
  "content": (
  <>
    <div className="mb-8">
      <p className="mb-4 text-justify">
        Imagine a world where young creators design their own video games, animate beloved characters, and tell interactive stories—all without writing a single line of code. Welcome to <strong>Scratch</strong>, MIT’s visual programming environment that transforms beginners into confident coders through a playful, drag-and-drop interface. In this post, we’ll dive into how Scratch helps learners master core programming principles while unleashing their creativity.
      </p>
      <p className="mb-4 text-justify">
        Whether you’re a student eager to build your first project or an educator aiming to spark curiosity, Scratch provides an accessible gateway to coding. Let’s explore what makes Scratch so empowering and how you can craft engaging projects from day one.
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
        What is Scratch? A Creative Coding Playground
      </h2>
      <p className="mb-4 text-justify">
        Scratch is a free, web-based platform (<a href="https://scratch.mit.edu" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">scratch.mit.edu</a>) that teaches programming through visual blocks. Instead of typing syntax, users snap together <strong>color-coded blocks</strong>—much like digital LEGO—to control animated characters called <strong>sprites</strong>. This intuitive design makes Scratch ideal for learners of all ages.
      </p>
      <p className="mb-4 text-justify">
        With millions of users worldwide, Scratch fosters problem-solving, logical thinking, and artistic expression. From a dancing cat to a space adventure, projects come alive instantly, keeping students motivated and curious.
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
        Exploring the Interface: Four Key Areas
      </h2>
      <p className="mb-4 text-justify">
        After creating a free Scratch account, learners land in the editor—a vibrant workspace divided into:
      </p>
      <ul className="list-disc pl-6 mb-4">
        <li><strong>Stage</strong>: The visual canvas where sprites perform actions.</li>
        <li><strong>Sprite List</strong>: A panel showing all characters or objects in your scene.</li>
        <li><strong>Block Palette</strong>: A library of blocks categorized by function (Motion, Looks, Sound, etc.).</li>
        <li><strong>Scripting Area</strong>: Where you drag blocks together to form scripts.</li>
      </ul>
      <p className="mb-4 text-justify">
        Customize backdrops, import or draw sprites, and mix-and-match blocks to experiment. The immediate visual feedback invites endless tinkering.
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
        Scratch Blocks: Your Coding Toolbox
      </h2>
      <p className="mb-4 text-justify">
        Scratch’s intuitive blocks break down programming into bite-sized pieces. Key categories include:
      </p>
      <ul className="list-disc pl-6 mb-4">
        <li><strong>Motion</strong>: Move sprites (e.g., “move 10 steps”).</li>
        <li><strong>Looks</strong>: Change appearance (e.g., “say Hello! for 2 seconds”).</li>
        <li><strong>Sound</strong>: Play audio effects and music.</li>
        <li><strong>Events</strong>: Start scripts (e.g., “when green flag clicked”).</li>
        <li><strong>Control</strong>: Structure your code (loops, conditionals).</li>
        <li><strong>Sensing</strong>: Detect interactions (touching colors, mouse clicks).</li>
        <li><strong>Operators</strong>: Perform math and logic operations.</li>
        <li><strong>Variables</strong>: Store and update data (e.g., scores, timers).</li>
      </ul>
      <p className="mb-4 text-justify">
        Drag blocks from the palette to the scripting area and snap them together to build behaviors. This hands-on model demystifies coding by showing cause and effect in real time.
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
        Building a Simple Maze Game: Step-by-Step
      </h2>
      <p className="mb-4 text-justify">
        Creating a maze game is a fun introduction to core concepts. Here’s a quick roadmap:
      </p>
      <ol className="list-decimal pl-6 mb-4">
        <li><strong>Design Your World</strong>: Select a player sprite and goal sprite. Use a backdrop featuring a maze or draw one yourself.</li>
        <li><strong>Enable Movement</strong>: Use Motion blocks with arrow-key events to move your sprite around the maze.</li>
        <li><strong>Handle Collisions</strong>: Implement Sensing and Control blocks (e.g., “if touching color [wall], go to start”) to reset on collision.</li>
        <li><strong>Declare Victory</strong>: Detect when the player touches the goal and celebrate with a message and sound.</li>
      </ol>
      <p className="mb-4 text-justify">
        Encourage students to add custom features—timers, scoring, or animated effects—for deeper engagement and personalization.
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
        Core Programming Concepts in Scratch
      </h2>
      <p className="mb-4 text-justify">
        While creating projects, learners internalize foundational ideas:
      </p>
      <ul className="list-disc pl-6 mb-4">
        <li><strong>Variables</strong>: Track data like scores or health.</li>
        <li><strong>Loops</strong>: Repeat actions efficiently.</li>
        <li><strong>Conditionals</strong>: Make decisions based on game state.</li>
        <li><strong>Events</strong>: Trigger responses to user actions.</li>
        <li><strong>Custom Blocks</strong>: Define reusable code modules.</li>
      </ul>
      <p className="mb-4 text-justify">
        These building blocks translate seamlessly to text-based languages later on, giving students a head start.
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
        Tips for Educators: Keep Students Engaged
      </h2>
      <p className="mb-4 text-justify">
        To foster a dynamic learning environment, try:
      </p>
      <ul className="list-disc pl-6 mb-4">
        <li><strong>Project Showcases</strong>: Highlight standout student creations to inspire peers.</li>
        <li><strong>Open Exploration</strong>: Allocate time for free play—serendipitous discoveries spark the best ideas.</li>
        <li><strong>Collaborative Pairing</strong>: Pair up learners to share techniques and build teamwork skills.</li>
        <li><strong>Challenge Prompts</strong>: Offer mini-challenges (e.g., “Add a timer” or “Design a mini boss fight”).</li>
      </ul>
      <p className="mb-4 text-justify">
        Celebrate experimentation and mistake-driven learning: every bug is an opportunity to explore.
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
        Conclusion
      </h2>
      <p className="mb-4 text-justify">
        Scratch transforms coding into a creative adventure. By mastering block-based programming, learners gain logical thinking, problem-solving skills, and the confidence to tackle more advanced languages. Ready to ignite imaginations? Dive into Scratch and watch coding curiosity flourish.
      </p>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
        Start Building:
        <a
          key="1"
          href="https://scratch.mit.edu/about"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-5 sm:ml-2 mt-1 sm:mt-0 px-2 py-1 font-medium text-red-600 hover:text-red-800 hover:bg-blue-100 rounded transition-colors"
        >
          [Scratch MIT]
        </a>
      </h2>

      <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
        Categories/Tags:
      </h2>
      <ul className="flex flex-wrap gap-2 text-sm text-blue-500 mb-4">
        <li>[Scratch]</li>
        <li>[Programming]</li>
        <li>[Education]</li>
        <li>[Kids]</li>
        <li>[Coding]</li>
        <li>[Game Development]</li>
        <li>[Computer Science]</li>
      </ul>
    </div>
  </>
)
  },
  {
    id: 8,
    title: "The Doomscroll Loop: Why Your Thumb Knows Something You Don’t",
    date: "July 15, 2026",
    description:
      "A neuroscience-based look at why doomscrolling hooks you — and the low-effort exits that work with your wiring instead of against it.",
    readTime: "6 min read",
    author: "Amit Kumar Ghosh",
    tags: ["Mental Health", "Neuroscience", "Attention", "Habits", "Self‑Care"],
    content: (
      <div className="mb-8">
        <p className="mb-4 text-justify">
          Remember the overzealous security guard in your head—the amygdala who
          jumps at every rustle of leaves? Doomscrolling is what happens when
          someone hands that guard a bottomless stack of surveillance monitors
          and tells him to keep watching. He will. He was built to. He’ll do it
          for three hours and feel worse the whole time, and he will not stop,
          because nothing in his job description says <em>when to stop</em>.
        </p>
        <p className="mb-4 text-justify">
          Here’s the uncomfortable part: your thumb isn’t weak. It’s obeying a
          system that’s working perfectly. Let’s look at what that system
          actually is.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          1. Wanting Is Not Liking
        </h2>
        <p className="mb-4 text-justify">
          The biggest myth about dopamine is that it’s the “pleasure chemical.”
          It isn’t. Berridge and Robinson spent decades separating two things we
          lazily fuse: <strong>wanting</strong> and <strong>liking</strong>.
          Dopamine drives the wanting—the pull, the reach, the <em>one more
          swipe</em>. Liking runs on a different system entirely (opioid and
          endocannabinoid circuits, mostly). Which explains the strangest
          feature of doomscrolling: you can want it intensely while enjoying it
          not at all. That’s not a contradiction. That’s two systems, and only
          one of them is holding the phone.
          <em>Low‑effort action:</em> Mid‑scroll, ask one question—“Am I
          enjoying this, or am I just reaching?” Don’t try to stop. Just answer
          it. Separating wanting from liking, out loud, is often enough to make
          the reach visible.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          2. The Slot Machine in Your Pocket
        </h2>
        <p className="mb-4 text-justify">
          Wolfram Schultz’s work showed that dopamine neurons don’t fire for
          reward. They fire for <strong>reward prediction error</strong>—the gap
          between what you expected and what you got. Predictable reward, no
          signal. Surprise, big signal. Now consider a feed. Most posts are
          nothing. Some are interesting. A rare few are genuinely alarming or
          genuinely great, and you cannot predict which. That’s a{" "}
          <strong>variable‑ratio schedule</strong>—the reinforcement pattern
          Ferster and Skinner documented in 1957 as the hardest of all to
          extinguish. Same math as a slot machine. The unpredictability isn’t a
          side effect of the feed. It <em>is</em> the product.
          <em>Low‑effort action:</em> Make the reward predictable and it loses
          its grip. Check one specific thing—a person, a topic, a single
          tab—instead of the feed. Predictable reward means no prediction error,
          which means no signal, which means a much smaller pull.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          3. Why <em>Doom</em>, Specifically
        </h2>
        <p className="mb-4 text-justify">
          Feeds don’t have to serve bad news. They do it because we grab it.
          Baumeister and colleagues laid this out in a paper titled, with rare
          academic bluntness, “Bad Is Stronger Than Good” (2001), and Rozin and
          Royzman mapped the same asymmetry as <strong>negativity bias</strong>{" "}
          the same year: threat information is processed faster, weighted
          heavier, and remembered longer than the pleasant kind. For an ancestor
          scanning a treeline, that asymmetry was survival. For you, scanning a
          feed engineered by people who measured exactly which headlines you
          couldn’t scroll past, it’s a vulnerability with a business model
          attached. Your guard isn’t malfunctioning. He’s just been aimed.
          <em>Low‑effort action:</em> Say the quiet part out loud—“This is being
          selected <em>for</em> me because I can’t look away.” Naming the
          mechanism does to it roughly what emotional labeling does to sadness
          (Lieberman et al., 2007): it moves the thing from something happening{" "}
          <em>to</em> you to something you’re <em>watching</em> happen.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          4. The Missing Stop Sign
        </h2>
        <p className="mb-4 text-justify">
          Every good habit loop has a boundary. Book has a last page. Episode
          has credits. Meal has an empty plate. These are{" "}
          <strong>stopping cues</strong>—the moment your brain gets permission
          to be done. Infinite scroll deletes the cue. There is no last page, so
          there’s no moment where continuing becomes a decision instead of a
          default. This isn’t a failure of your willpower; you never got the
          prompt where willpower gets to speak. You can’t decide to stop if
          nothing ever asks.
          <em>Low‑effort action:</em> Build the cue yourself, on the outside
          where the app can’t delete it. A timer. A glass of water you’re
          finished with. Standing up. It doesn’t need to be strict—it just needs
          to be a <em>moment</em>, because a moment is where a decision can
          happen.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          5. The Cost You Don’t Feel Until Later
        </h2>
        <p className="mb-4 text-justify">
          Killingsworth and Gilbert (2010) pinged thousands of people at random
          through their days and found something durable: a wandering mind is an
          unhappy mind—and it predicted lower mood <em>afterward</em>, not just
          alongside. The mind‑wandering came first. Doomscrolling is
          mind‑wandering with a supplier. And it hides its own cost, because the
          bill doesn’t arrive during—it arrives forty minutes later, as flatness
          you’ll probably blame on something else entirely.
          <em>Low‑effort action:</em> Timestamp it. Note the mood you started
          with and the mood you ended with, once. Not to shame yourself—to close
          the loop between the action and its cost, which is the connection the
          design is quietly counting on you never making.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Why This Matters (Without the Hype)
        </h2>
        <p className="mb-4 text-justify">
          Honest caveat: the neuroscience above—wanting versus liking,
          prediction error, negativity bias—is well established. The research on{" "}
          <em>doomscrolling specifically</em> is younger, mostly correlational,
          and it can’t cleanly tell you whether the scrolling causes the low mood
          or the low mood drives the scrolling. Probably both, feeding each
          other. Anyone selling you a clean causal arrow is selling you
          something. So don’t treat this as a moral verdict on your phone. Treat
          it as a <strong>wiring diagram</strong>. You’re not weak‑willed for
          losing three hours to a feed built by teams who tested it against
          millions of people to make sure you would. That’s not a fair fight, and
          pretending it is just adds shame on top of the flatness.
        </p>

        <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
          Final Thought
        </h2>
        <p className="mb-4 text-justify">
          The loop isn’t a character flaw. It’s a wanting system that predates
          agriculture, wired to a threat detector that predates language,
          plugged into an interface designed last quarter to exploit both. You’re
          not going to out‑discipline that. But you don’t have to. You just have
          to give the guard a stopping cue—something the feed refuses to give
          him—and let him finally clock out. He’s been on shift a long time.
        </p>
      </div>
    ),
  },
  {
    id: 9,
    title: "B-Tree, GiST & GIN: Database Indexes Made Simple",
    date: "July 27, 2026",
    description:
      "A plain-English tour of the three index types that make databases fast — how each one actually stores your data, when to reach for it, and the mistakes that quietly ruin query performance.",
    readTime: "14 min read",
    author: "Amit Kumar Ghosh",
    tags: [
      "Database",
      "PostgreSQL",
      "Indexing",
      "B-Tree",
      "GIN",
      "GiST",
      "Performance",
      "Backend",
    ],
    content: (
      <>
        <div className="mb-8">
          <p className="mb-4 text-justify">
            Every backend developer eventually hits the same wall: a query that
            ran in 5 milliseconds on your laptop now takes 12 seconds in
            production. Someone says &quot;just add an index&quot; — you add
            one, and nothing changes. The problem usually isn&apos;t that you
            forgot an index. It&apos;s that you added the <em>wrong kind</em>.
            Postgres ships several index types, and the three that matter most
            are B-Tree, GiST, and GIN. Let&apos;s understand what each one
            actually does, in plain language.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            The One Idea That Explains Everything
          </h2>
          <p className="mb-4 text-justify">
            An index is not a magic &quot;make it fast&quot; switch. It is a
            second copy of your data, stored in a shape that answers one
            specific kind of question quickly. Think of a physical book. The
            page numbers let you jump to page 240 instantly — that&apos;s a
            sorted structure. The index at the back lets you find every page
            mentioning &quot;photosynthesis&quot; — that&apos;s a completely
            different structure. Neither one can do the other&apos;s job. Same
            book, same content, two different shapes for two different
            questions.
          </p>
          <p className="mb-4 text-justify">
            So the real question is never &quot;should I add an index?&quot; It
            is <strong>&quot;what shape is my question?&quot;</strong> Sorted
            order, overlapping regions, or membership. Those three shapes map
            almost perfectly onto B-Tree, GiST, and GIN.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            B-Tree: The Sorted Bookshelf
          </h2>
          <p className="mb-4 text-justify">
            B-Tree is the default index in every database you have ever used. If
            you write <code className="bg-gray-100 px-1 rounded">CREATE INDEX</code>{" "}
            without saying anything else, you get a B-Tree. It works on one
            simple assumption: your values can be lined up in a single sorted
            order.
          </p>
          <p className="mb-4 text-justify">
            Picture a librarian who keeps every book sorted by number. To find
            book 63, she doesn&apos;t walk the shelves. She checks a sign that
            says &quot;below 50 go left, above 50 go right,&quot; then another
            sign, then another. Three or four signs later she is holding the
            book. That is a B-Tree: a short, wide tree of signposts sitting on
            top of a sorted list.
          </p>

          <div className="my-6 flex justify-center">
            <svg
              viewBox="0 0 700 320"
              className="w-full max-w-2xl"
              role="img"
              aria-label="A B-Tree with a root node, two internal nodes, four sorted leaf nodes, and pointers down to the table rows on disk."
            >
              <defs>
                <marker
                  id="bt-arrow"
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto"
                >
                  <path d="M0 0 L10 5 L0 10 z" fill="#94a3b8" />
                </marker>
              </defs>

              <rect x="290" y="20" width="120" height="42" rx="8" fill="#dbeafe" stroke="#2563eb" strokeWidth="1.5" />
              <text x="350" y="41" textAnchor="middle" dominantBaseline="central" fontSize="15" fontWeight="600" fill="#1e3a8a">50</text>

              <line x1="350" y1="62" x2="180" y2="98" stroke="#94a3b8" strokeWidth="1.2" markerEnd="url(#bt-arrow)" />
              <line x1="350" y1="62" x2="520" y2="98" stroke="#94a3b8" strokeWidth="1.2" markerEnd="url(#bt-arrow)" />

              <rect x="120" y="100" width="120" height="42" rx="8" fill="#dbeafe" stroke="#2563eb" strokeWidth="1.5" />
              <text x="180" y="121" textAnchor="middle" dominantBaseline="central" fontSize="15" fontWeight="600" fill="#1e3a8a">20</text>
              <rect x="460" y="100" width="120" height="42" rx="8" fill="#dbeafe" stroke="#2563eb" strokeWidth="1.5" />
              <text x="520" y="121" textAnchor="middle" dominantBaseline="central" fontSize="15" fontWeight="600" fill="#1e3a8a">80</text>

              <line x1="180" y1="142" x2="100" y2="176" stroke="#94a3b8" strokeWidth="1.2" markerEnd="url(#bt-arrow)" />
              <line x1="180" y1="142" x2="260" y2="176" stroke="#94a3b8" strokeWidth="1.2" markerEnd="url(#bt-arrow)" />
              <line x1="520" y1="142" x2="440" y2="176" stroke="#94a3b8" strokeWidth="1.2" markerEnd="url(#bt-arrow)" />
              <line x1="520" y1="142" x2="600" y2="176" stroke="#94a3b8" strokeWidth="1.2" markerEnd="url(#bt-arrow)" />

              <rect x="30" y="178" width="140" height="40" rx="8" fill="#eff6ff" stroke="#60a5fa" strokeWidth="1.2" />
              <text x="100" y="198" textAnchor="middle" dominantBaseline="central" fontSize="13" fill="#1e40af">1 – 19</text>
              <rect x="190" y="178" width="140" height="40" rx="8" fill="#eff6ff" stroke="#60a5fa" strokeWidth="1.2" />
              <text x="260" y="198" textAnchor="middle" dominantBaseline="central" fontSize="13" fill="#1e40af">20 – 49</text>
              <rect x="370" y="178" width="140" height="40" rx="8" fill="#eff6ff" stroke="#60a5fa" strokeWidth="1.2" />
              <text x="440" y="198" textAnchor="middle" dominantBaseline="central" fontSize="13" fill="#1e40af">50 – 79</text>
              <rect x="530" y="178" width="140" height="40" rx="8" fill="#eff6ff" stroke="#60a5fa" strokeWidth="1.2" />
              <text x="600" y="198" textAnchor="middle" dominantBaseline="central" fontSize="13" fill="#1e40af">80 +</text>

              <line x1="170" y1="198" x2="190" y2="198" stroke="#60a5fa" strokeWidth="1.2" strokeDasharray="3 3" />
              <line x1="330" y1="198" x2="370" y2="198" stroke="#60a5fa" strokeWidth="1.2" strokeDasharray="3 3" />
              <line x1="510" y1="198" x2="530" y2="198" stroke="#60a5fa" strokeWidth="1.2" strokeDasharray="3 3" />

              <line x1="100" y1="218" x2="100" y2="252" stroke="#cbd5e1" strokeWidth="1" markerEnd="url(#bt-arrow)" />
              <line x1="260" y1="218" x2="260" y2="252" stroke="#cbd5e1" strokeWidth="1" markerEnd="url(#bt-arrow)" />
              <line x1="440" y1="218" x2="440" y2="252" stroke="#cbd5e1" strokeWidth="1" markerEnd="url(#bt-arrow)" />
              <line x1="600" y1="218" x2="600" y2="252" stroke="#cbd5e1" strokeWidth="1" markerEnd="url(#bt-arrow)" />

              <rect x="30" y="254" width="640" height="38" rx="8" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.2" />
              <text x="350" y="273" textAnchor="middle" dominantBaseline="central" fontSize="13" fill="#475569">Actual table rows on disk</text>

              <text x="350" y="310" textAnchor="middle" fontSize="12" fill="#64748b">Leaves are chained together, so a range scan is just a walk sideways</text>
            </svg>
          </div>

          <p className="mb-4 text-justify">
            Two things make this fast. First, each node is one 8 KB disk page
            holding hundreds of keys, so the tree stays about 3–4 levels deep
            even for a billion rows. Second, because the leaves are stored in
            sorted order and linked to each other, a range query doesn&apos;t
            restart the search for every row — it finds the starting point once
            and then walks.
          </p>

          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm mb-4">
            <code>{`-- The default. Great for these query shapes:
CREATE INDEX idx_orders_created ON orders (created_at);

SELECT * FROM orders WHERE created_at = '2026-07-01';          -- equality
SELECT * FROM orders WHERE created_at BETWEEN '2026-01-01'
                                          AND '2026-06-30';     -- range
SELECT * FROM orders ORDER BY created_at DESC LIMIT 20;         -- sorting, free
SELECT max(created_at) FROM orders;                             -- one descent
SELECT * FROM users WHERE email LIKE 'amit%';                   -- prefix = range`}</code>
          </pre>

          <p className="mb-4 text-justify">
            That last line is worth pausing on.{" "}
            <code className="bg-gray-100 px-1 rounded">LIKE &apos;amit%&apos;</code>{" "}
            works because a prefix <em>is</em> a range — everything between
            &quot;amit&quot; and &quot;amiu&quot;. But{" "}
            <code className="bg-gray-100 px-1 rounded">LIKE &apos;%amit%&apos;</code>{" "}
            cannot use a B-Tree at all, because there is no starting point to
            jump to. Hold that thought; GIN fixes it later.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            The Composite Index Trap
          </h2>
          <p className="mb-4 text-justify">
            A multi-column B-Tree sorts lexicographically — like a phone book
            sorted by last name, then first name. This single fact explains the
            most common indexing bug in production:
          </p>

          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm mb-4">
            <code>{`CREATE INDEX idx_ab ON events (tenant_id, created_at);

-- Uses the index
SELECT * FROM events WHERE tenant_id = 42;
SELECT * FROM events WHERE tenant_id = 42 AND created_at > now() - interval '1 day';

-- Does NOT use the index (no leading column)
SELECT * FROM events WHERE created_at > now() - interval '1 day';`}</code>
          </pre>

          <p className="mb-4 text-justify">
            In a phone book you cannot find everyone named &quot;Amit&quot;
            without scanning every page, because first names are only sorted
            <em> within</em> each last name. Same rule. Column order in a
            composite index is a decision, not a formality.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Where B-Tree Gives Up
          </h2>
          <p className="mb-4 text-justify">
            B-Tree knows exactly one trick: sorted order. Which means it is
            helpless the moment your question has no natural line to sort along.
            &quot;Which delivery zones overlap this neighbourhood?&quot;
            &quot;Which meeting rooms are double-booked?&quot; &quot;Which
            articles mention both &apos;rust&apos; and &apos;compiler&apos;?&quot;
            You cannot sort shapes on a line. You cannot sort a bag of words on
            a line. Different question shape, different index.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            GiST: Drawing Boxes Around Things
          </h2>
          <p className="mb-4 text-justify">
            GiST stands for Generalized Search Tree, and the &quot;generalized&quot;
            part is the point: it isn&apos;t really an index, it&apos;s a
            <em> kit</em> for building one. Postgres supplies the tree
            machinery; the data type supplies the meaning.
          </p>
          <p className="mb-4 text-justify">
            The intuition is a moving company packing a warehouse. Instead of
            sorting items, they group nearby things into labelled crates:
            &quot;this crate covers the north-west corner.&quot; Bigger crates
            contain smaller crates. To find a specific vase, you only open
            crates whose label <em>could</em> contain it. Every internal node in
            a GiST index holds a claim about everything beneath it — usually a
            bounding box.
          </p>

          <div className="my-6 flex justify-center">
            <svg
              viewBox="0 0 700 300"
              className="w-full max-w-2xl"
              role="img"
              aria-label="Two bounding boxes drawn around groups of shapes. The boxes overlap in the middle, and a search point lands inside the overlap, meaning both branches must be opened."
            >
              <rect x="30" y="20" width="640" height="230" rx="10" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.2" strokeDasharray="5 4" />
              <text x="44" y="40" fontSize="12" fill="#64748b">Root: covers everything</text>

              <rect x="60" y="60" width="290" height="165" rx="8" fill="#d1fae5" fillOpacity="0.55" stroke="#059669" strokeWidth="1.5" strokeDasharray="6 4" />
              <text x="74" y="80" fontSize="13" fontWeight="600" fill="#065f46">Crate A</text>

              <rect x="300" y="95" width="310" height="130" rx="8" fill="#ccfbf1" fillOpacity="0.55" stroke="#0d9488" strokeWidth="1.5" strokeDasharray="6 4" />
              <text x="510" y="115" fontSize="13" fontWeight="600" fill="#115e59">Crate B</text>

              <circle cx="120" cy="140" r="16" fill="#34d399" stroke="#047857" strokeWidth="1.2" />
              <rect x="170" y="165" width="34" height="34" rx="4" fill="#34d399" stroke="#047857" strokeWidth="1.2" />
              <polygon points="245,110 265,148 225,148" fill="#34d399" stroke="#047857" strokeWidth="1.2" />

              <circle cx="420" cy="150" r="16" fill="#2dd4bf" stroke="#0f766e" strokeWidth="1.2" />
              <rect x="500" y="150" width="34" height="34" rx="4" fill="#2dd4bf" stroke="#0f766e" strokeWidth="1.2" />
              <polygon points="570,130 590,168 550,168" fill="#2dd4bf" stroke="#0f766e" strokeWidth="1.2" />

              <rect x="312" y="180" width="26" height="26" rx="3" fill="#fecaca" stroke="#dc2626" strokeWidth="1.6" />
              <text x="350" y="196" fontSize="12" fill="#b91c1c">your search lands here</text>

              <text x="350" y="278" textAnchor="middle" fontSize="12" fill="#64748b">Crates can overlap — so this one search has to open both A and B</text>
            </svg>
          </div>

          <p className="mb-4 text-justify">
            Notice the overlap. This is the fundamental difference from a
            B-Tree, where every value lives in exactly one place. In GiST,
            boxes can overlap, so one search may have to walk down several
            branches at once. GiST is also <em>lossy</em>: it hands back a list
            of &quot;maybes&quot; and the database re-checks each one against the
            real value. Slightly less efficient, dramatically more capable.
          </p>

          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm mb-4">
            <code>{`-- 1. Geometry / maps (PostGIS)
CREATE INDEX idx_zones_geom ON zones USING GIST (boundary);
SELECT * FROM zones WHERE boundary && ST_MakeEnvelope(72.8, 19.0, 72.9, 19.1, 4326);

-- 2. Ranges: "which bookings overlap this window?"
CREATE INDEX idx_bookings_period ON bookings USING GIST (period);
SELECT * FROM bookings WHERE period && tsrange('2026-08-01', '2026-08-05');

-- 3. The killer feature: the database enforces "no double booking"
ALTER TABLE bookings ADD CONSTRAINT no_overlap
  EXCLUDE USING GIST (room_id WITH =, period WITH &&);

-- 4. Nearest neighbour, sorted by real distance
SELECT name FROM stores ORDER BY location <-> ST_Point(72.87, 19.07) LIMIT 5;`}</code>
          </pre>

          <p className="mb-4 text-justify">
            That third example is my favourite thing in Postgres. An exclusion
            constraint means double-booking becomes <em>structurally
            impossible</em> — no race conditions, no application-level locking,
            no &quot;we check before inserting&quot; logic that breaks under
            concurrency. And the fourth one, nearest-neighbour ordering, is
            something no B-Tree can do at any price.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            GIN: The Index at the Back of the Book
          </h2>
          <p className="mb-4 text-justify">
            GIN stands for Generalized Inverted Index, and it is built for
            values that contain many things: an article contains many words, a
            row contains an array of tags, a JSON document contains many keys.
            The trick is right there in the name — it turns the relationship
            inside out.
          </p>
          <p className="mb-4 text-justify">
            Normally you go from a row to its contents. GIN flips it: from each
            piece of content to the list of rows containing it. Exactly like the
            index at the back of a textbook.
          </p>

          <div className="my-6 flex justify-center">
            <svg
              viewBox="0 0 700 290"
              className="w-full max-w-2xl"
              role="img"
              aria-label="Three rows of text on the left are inverted into a list on the right mapping each word to the row numbers that contain it."
            >
              <defs>
                <marker
                  id="gin-arrow"
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="7"
                  markerHeight="7"
                  orient="auto"
                >
                  <path d="M0 0 L10 5 L0 10 z" fill="#d97706" />
                </marker>
              </defs>

              <text x="40" y="32" fontSize="13" fontWeight="600" fill="#78350f">What you store</text>

              <rect x="30" y="46" width="240" height="42" rx="8" fill="#fffbeb" stroke="#fbbf24" strokeWidth="1.2" />
              <text x="46" y="67" dominantBaseline="central" fontSize="13" fill="#78350f">row 1 · the quick fox</text>
              <rect x="30" y="98" width="240" height="42" rx="8" fill="#fffbeb" stroke="#fbbf24" strokeWidth="1.2" />
              <text x="46" y="119" dominantBaseline="central" fontSize="13" fill="#78350f">row 2 · the lazy dog</text>
              <rect x="30" y="150" width="240" height="42" rx="8" fill="#fffbeb" stroke="#fbbf24" strokeWidth="1.2" />
              <text x="46" y="171" dominantBaseline="central" fontSize="13" fill="#78350f">row 3 · quick dog runs</text>

              <line x1="290" y1="119" x2="380" y2="119" stroke="#d97706" strokeWidth="2" markerEnd="url(#gin-arrow)" />
              <text x="335" y="104" textAnchor="middle" fontSize="12" fontWeight="600" fill="#b45309">invert</text>

              <text x="410" y="32" fontSize="13" fontWeight="600" fill="#78350f">What GIN keeps</text>

              <rect x="400" y="46" width="270" height="34" rx="6" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1.2" />
              <text x="416" y="63" dominantBaseline="central" fontSize="13" fill="#78350f">dog</text>
              <text x="654" y="63" textAnchor="end" dominantBaseline="central" fontSize="13" fill="#92400e">2, 3</text>

              <rect x="400" y="86" width="270" height="34" rx="6" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1.2" />
              <text x="416" y="103" dominantBaseline="central" fontSize="13" fill="#78350f">quick</text>
              <text x="654" y="103" textAnchor="end" dominantBaseline="central" fontSize="13" fill="#92400e">1, 3</text>

              <rect x="400" y="126" width="270" height="34" rx="6" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1.2" />
              <text x="416" y="143" dominantBaseline="central" fontSize="13" fill="#78350f">the</text>
              <text x="654" y="143" textAnchor="end" dominantBaseline="central" fontSize="13" fill="#92400e">1, 2</text>

              <rect x="400" y="166" width="270" height="34" rx="6" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1.2" />
              <text x="416" y="183" dominantBaseline="central" fontSize="13" fill="#78350f">fox</text>
              <text x="654" y="183" textAnchor="end" dominantBaseline="central" fontSize="13" fill="#92400e">1</text>

              <text x="350" y="240" textAnchor="middle" fontSize="12" fill="#64748b">Searching &quot;quick AND dog&quot; = fetch two small lists, intersect them, done</text>
              <text x="350" y="264" textAnchor="middle" fontSize="12" fill="#64748b">One new row touches many keys — which is why writes cost more</text>
            </svg>
          </div>

          <p className="mb-4 text-justify">
            Searching for two words becomes a tiny set operation instead of a
            table scan. That is why full-text search, tag filtering, and JSON
            queries all lean on GIN.
          </p>

          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm mb-4">
            <code>{`-- Full-text search
CREATE INDEX idx_posts_search ON posts USING GIN (to_tsvector('english', body));
SELECT * FROM posts
 WHERE to_tsvector('english', body) @@ websearch_to_tsquery('rust compiler');

-- Array containment: rows tagged with BOTH values
CREATE INDEX idx_posts_tags ON posts USING GIN (tags);
SELECT * FROM posts WHERE tags @> ARRAY['database', 'postgres'];

-- JSONB. jsonb_path_ops is smaller and faster if you only need @>
CREATE INDEX idx_events_payload ON events USING GIN (payload jsonb_path_ops);
SELECT * FROM events WHERE payload @> '{"status": "failed"}';

-- And the fix for that %substring% problem from earlier
CREATE EXTENSION IF NOT EXISTS pg_trgm;
CREATE INDEX idx_users_name_trgm ON users USING GIN (full_name gin_trgm_ops);
SELECT * FROM users WHERE full_name ILIKE '%mit kum%';   -- now indexed!`}</code>
          </pre>

          <p className="mb-4 text-justify">
            The trigram index in that last block is genuinely magic the first
            time you see it. It chops every string into three-character pieces
            (&quot;amit&quot; becomes &quot;ami&quot;, &quot;mit&quot;) and
            indexes those, so a search from the middle of a word suddenly has
            something to look up. Here is roughly what that does to a
            fuzzy-search query on a million-row table:
          </p>

          <div className="my-6 flex justify-center">
            <svg
              viewBox="0 0 700 250"
              className="w-full max-w-2xl"
              role="img"
              aria-label="Bar chart comparing query time for a fuzzy substring search: no index 2400 milliseconds, plain B-Tree 2380 milliseconds, GiST trigram 180 milliseconds, GIN trigram 35 milliseconds."
            >
              <text x="10" y="22" fontSize="13" fontWeight="600" fill="#334155">Fuzzy search: ILIKE &apos;%mit kum%&apos; on 1M rows</text>

              <text x="185" y="55" textAnchor="end" dominantBaseline="central" fontSize="12" fill="#475569">No index</text>
              <rect x="195" y="40" width="420" height="30" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1" />
              <text x="625" y="55" dominantBaseline="central" fontSize="12" fill="#475569">2400 ms</text>

              <text x="185" y="103" textAnchor="end" dominantBaseline="central" fontSize="12" fill="#475569">B-Tree</text>
              <rect x="195" y="88" width="416" height="30" rx="4" fill="#dbeafe" stroke="#2563eb" strokeWidth="1" />
              <text x="621" y="103" dominantBaseline="central" fontSize="12" fill="#475569">2380 ms</text>

              <text x="185" y="151" textAnchor="end" dominantBaseline="central" fontSize="12" fill="#475569">GiST + trigram</text>
              <rect x="195" y="136" width="32" height="30" rx="4" fill="#d1fae5" stroke="#059669" strokeWidth="1" />
              <text x="237" y="151" dominantBaseline="central" fontSize="12" fill="#475569">180 ms</text>

              <text x="185" y="199" textAnchor="end" dominantBaseline="central" fontSize="12" fill="#475569">GIN + trigram</text>
              <rect x="195" y="184" width="8" height="30" rx="2" fill="#fde68a" stroke="#d97706" strokeWidth="1" />
              <text x="213" y="199" dominantBaseline="central" fontSize="12" fill="#475569">35 ms</text>

              <text x="10" y="238" fontSize="11" fill="#94a3b8">Illustrative figures — the point is the order of magnitude, not the exact numbers. Always measure your own data.</text>
            </svg>
          </div>

          <p className="mb-4 text-justify">
            Look at the second bar. The B-Tree index exists, it is sitting right
            there on the correct column, and it does nothing. This is the
            &quot;I added an index and it didn&apos;t help&quot; moment,
            visualised.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            The Catch: GIN Writes Are Slow
          </h2>
          <p className="mb-4 text-justify">
            Nothing is free. When you insert one blog post, a B-Tree adds one
            entry. A GIN index adds one entry <em>per distinct word</em> —
            potentially hundreds. On write-heavy tables this hurts. Postgres
            softens the blow with a pending list that batches new entries and
            flushes them later, but that has its own cost: every read must also
            scan that unsorted pending area, which is why GIN query times can
            get spiky right before a vacuum.
          </p>

          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm mb-4">
            <code>{`-- Bulk loading? Turn off the pending list, load, then rebuild.
ALTER INDEX idx_posts_search SET (fastupdate = off);

-- Check what your indexes actually cost you
SELECT indexrelname,
       pg_size_pretty(pg_relation_size(indexrelid)) AS size,
       idx_scan AS times_used
  FROM pg_stat_user_indexes
 WHERE relname = 'posts'
 ORDER BY pg_relation_size(indexrelid) DESC;`}</code>
          </pre>

          <p className="mb-4 text-justify">
            Run that second query on your production database sometime. Almost
            everyone finds at least one large index with{" "}
            <code className="bg-gray-100 px-1 rounded">times_used = 0</code> —
            pure write overhead and disk cost, buying nothing.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Quick Reference: Which One Do I Want?
          </h2>

          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">
                    Your question sounds like
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">
                    Use
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">
                    Equals, greater than, between, sort, starts-with
                  </td>
                  <td className="border border-gray-300 px-3 py-2 font-medium text-blue-700">
                    B-Tree
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2">
                    Do these dates / rooms / shapes overlap?
                  </td>
                  <td className="border border-gray-300 px-3 py-2 font-medium text-emerald-700">
                    GiST
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">
                    What are the 5 nearest points to me?
                  </td>
                  <td className="border border-gray-300 px-3 py-2 font-medium text-emerald-700">
                    GiST
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2">
                    Prevent double-booking at the database level
                  </td>
                  <td className="border border-gray-300 px-3 py-2 font-medium text-emerald-700">
                    GiST (exclusion)
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">
                    Search text, match tags, query inside JSON
                  </td>
                  <td className="border border-gray-300 px-3 py-2 font-medium text-amber-700">
                    GIN
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2">
                    Fuzzy / substring matching anywhere in a string
                  </td>
                  <td className="border border-gray-300 px-3 py-2 font-medium text-amber-700">
                    GIN + pg_trgm
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">
                    Huge append-only table, data already in time order
                  </td>
                  <td className="border border-gray-300 px-3 py-2 font-medium text-gray-700">
                    BRIN (the cheap fourth option)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Four Mistakes Worth Avoiding
          </h2>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li className="text-justify">
              <strong>Indexing every column individually.</strong> Postgres can
              combine indexes, but one well-ordered composite index usually
              beats five single-column ones — and costs far less on writes.
            </li>
            <li className="text-justify">
              <strong>Wrapping the indexed column in a function.</strong>{" "}
              <code className="bg-gray-100 px-1 rounded">WHERE lower(email) = ...</code>{" "}
              skips a plain index on{" "}
              <code className="bg-gray-100 px-1 rounded">email</code>. Either
              index the expression itself, or stop transforming the column.
            </li>
            <li className="text-justify">
              <strong>Indexing a column with three distinct values.</strong> If
              a query returns 30% of the table, jumping back and forth to the
              heap is slower than just reading the whole thing. The planner
              knows this, which is why it ignores your index.
            </li>
            <li className="text-justify">
              <strong>Trusting timings instead of plans.</strong> Run{" "}
              <code className="bg-gray-100 px-1 rounded">
                EXPLAIN (ANALYZE, BUFFERS)
              </code>
              . Buffer counts tell you how much data was actually touched, which
              is the number that survives a warm cache and a different machine.
            </li>
          </ul>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Conclusion
          </h2>
          <p className="mb-4 text-justify">
            Indexes stop being intimidating once you stop thinking of them as a
            performance setting and start thinking of them as data structures
            with opinions. B-Tree believes everything can be sorted on a line,
            and it is brilliant whenever that is true. GiST believes things live
            in regions that can overlap, which unlocks maps, calendars, and
            nearest-neighbour search. GIN believes a value is a bag of smaller
            pieces, which is what makes text search and JSON queries fast.
          </p>
          <p className="mb-4 text-justify">
            So next time a query crawls, don&apos;t reach for{" "}
            <code className="bg-gray-100 px-1 rounded">CREATE INDEX</code> on
            reflex. Read the{" "}
            <code className="bg-gray-100 px-1 rounded">WHERE</code> clause out
            loud and ask what shape the question is. The right index almost
            always announces itself.
          </p>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Further Reading:
            <a
              key="1"
              href="https://www.postgresql.org/docs/current/indexes-types.html"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-5 sm:ml-2 mt-1 sm:mt-0 px-2 py-1 font-medium text-red-600 hover:text-red-800 hover:bg-blue-100 rounded transition-colors"
            >
              [Link]
            </a>
          </h2>

          <h2 className="font-bold text-xl mb-2 text-blue-600 hover:text-blue-800">
            Categories/Tags:
          </h2>
          <ul className="flex flex-wrap gap-2 text-sm text-blue-500 mb-4">
            <li>[Database]</li>
            <li>[PostgreSQL]</li>
            <li>[Indexing]</li>
            <li>[B-Tree]</li>
            <li>[GIN]</li>
            <li>[GiST]</li>
            <li>[Performance]</li>
            <li>[Backend]</li>
          </ul>
        </div>
      </>
    ),
  }

];

export const blogs = [
  {
    id: 1,
    title: "Why Has Reading Become a Struggle?",
    date: "March 15, 2024",
    description:
      "Exploring the challenges of reading in the digital age and how technology impacts literacy.",
    readTime: "15 min read",
    author: "Amit Kumar Ghosh",
    tags: ["reading", "Education", "Literacy", "Technology"],
  },
  // Add more blogs here...
];
